import { GetterTree } from "vuex/types";
import { RootState } from "@/store/types";
import { BroadcastState } from "./types";
import _ from "lodash";

/**
 * Getters for the Broadcast module.
 * Provides computed access to broadcast data and configuration.
 */
const getters: GetterTree<BroadcastState, RootState> = {
  /**
   * Returns the currently selected broadcast timestamp ID.
   *
   * @param {BroadcastState} state - Module state
   * @returns {string} Selected broadcast timestamp
   */
  selectedBroadcastId(state, getters, rootState) {
    return state.selectedBroadcastId;
  },

  /**
   * Returns the currently selected broadcast object.
   *
   * @param {BroadcastState} state - Module state
   * @returns {IBroadcast|null} Selected broadcast or null
   */
  selectedBroadcast(state, getters, rootState) {
    return state.selectedBroadcast;
  },

  /**
   * Returns all broadcasts as an object indexed by timestamp.
   *
   * @param {BroadcastState} state - Module state
   * @returns {Object} Broadcasts object
   */
  broadcastObjects(state, getters, rootState) {
    return state.broadcastObjects;
  },

  /**
   * Returns all broadcasts as an array.
   *
   * @param {BroadcastState} state - Module state
   * @returns {Array<IBroadcast>} Broadcasts array
   */
  broadcastArray(state, getters, rootState) {
    const BroadcastArray = Object.values(state.broadcastObjects);
    return BroadcastArray;
  },

  /**
   * Returns preset audience segments for targeting.
   *
   * @param {BroadcastState} state - Module state
   * @param {Object} getters - Other getters
   * @param {RootState} rootState - Root state
   * @returns {Object} Preset segments configuration
   */
  presetSegments(state, getters, rootState) {
    const segments = _.get(rootState, "modules.broadcast.presetSegments", {});

    return segments;
  },

  /**
   * Parses and returns Facebook test user IDs for broadcast testing.
   * Format: "label:id;label2:id2"
   *
   * @param {BroadcastState} state - Module state
   * @param {Object} getters - Other getters
   * @param {RootState} rootState - Root state
   * @returns {Array<{label: string, value: string}>} Parsed test users
   */
  facebookTestUserIds(state, getters, rootState) {
    const stringTestUsersId = _.get(rootState, "modules.broadcast.testUsersId", "");

    const arrayTestUsers: string[] = stringTestUsersId.split(";");
    if (Array.isArray(arrayTestUsers)) {
      const facebookTestUsers = arrayTestUsers.map((user) => {
        if (user) {
          const userIdArray = user.split(":");
          const variablesAreValid = userIdArray[0] && userIdArray[1];
          if (variablesAreValid) {
            const userObject = {
              label: userIdArray[0].trim(),
              value: userIdArray[1].trim(),
            };
            return userObject;
          }
        }
      });
      return _.compact(facebookTestUsers);
    } else {
      return [];
    }
  },

  /**
   * Returns the Facebook page ID for broadcast testing.
   *
   * @param {BroadcastState} state - Module state
   * @param {Object} getters - Other getters
   * @param {RootState} rootState - Root state
   * @returns {string} Test page ID
   */
  broadcastTestPageId(state, getters, rootState) {
    const testPageId = _.get(rootState, "modules.broadcast.testPageId", "");

    return testPageId;
  },

  /**
   * Parses and returns available broadcast tags for Facebook compliance.
   * Format: "TAG1;TAG2;TAG3"
   *
   * @param {BroadcastState} state - Module state
   * @param {Object} getters - Other getters
   * @param {RootState} rootState - Root state
   * @returns {Array<string>} Available broadcast tags
   */
  broadcastTags(state, getters, rootState) {
    const stringBroadcastTags: string = _.get(
      rootState,
      "modules.broadcast.broadcastTags",
      "NON_PROMOTIONAL_SUBSCRIPTION;"
    );
    const broadcastTags = stringBroadcastTags.split(";");

    return _.compact(broadcastTags);
  },
};

export default getters;
